import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

interface RPMData {
  category: string;
  avgRPM: number;
  suggestedPlacements: string[];
  keywords: string[];
}

const useRPMOptimization = () => {
  const location = useLocation();
  const [rpmData, setRPMData] = useState<RPMData | null>(null);
  const [optimalAdCount, setOptimalAdCount] = useState(3);

  // RPM data for different AI categories
  const rpmCategories: Record<string, RPMData> = {
    'chatgpt': {
      category: 'AI Writing & LLMs',
      avgRPM: 8.5,
      suggestedPlacements: ['header', 'sidebar', 'in-content'],
      keywords: ['chatgpt', 'openai', 'gpt-4', 'llm', 'language-model']
    },
    'midjourney': {
      category: 'AI Art & Image',
      avgRPM: 7.2,
      suggestedPlacements: ['header', 'gallery', 'tool-detail'],
      keywords: ['midjourney', 'ai-art', 'stable-diffusion', 'image-generation']
    },
    'copilot': {
      category: 'AI Coding',
      avgRPM: 9.1,
      suggestedPlacements: ['sidebar', 'in-code', 'comparison'],
      keywords: ['github-copilot', 'ai-coding', 'code-generation', 'dev-tools']
    },
    'video-ai': {
      category: 'AI Video',
      avgRPM: 6.8,
      suggestedPlacements: ['header', 'video-player', 'tool-list'],
      keywords: ['runway-ml', 'ai-video', 'video-editing', 'synthesia']
    },
    'ai-tools': {
      category: 'AI Directory',
      avgRPM: 5.5,
      suggestedPlacements: ['header', 'footer', 'category-list'],
      keywords: ['ai-tools', 'directory', 'software-catalog', 'tool-comparison']
    }
  };

  // Analyze current page for optimal RPM
  const analyzePage = useCallback(() => {
    const path = location.pathname.toLowerCase();
    
    for (const [key, data] of Object.entries(rpmCategories)) {
      if (path.includes(key)) {
        setRPMData(data);
        
        // Set optimal ad count based on RPM
        if (data.avgRPM > 8) setOptimalAdCount(4); // High RPM pages
        else if (data.avgRPM > 6) setOptimalAdCount(3); // Medium RPM
        else setOptimalAdCount(2); // Lower RPM
        
        return;
      }
    }
    
    // Default to AI tools directory
    setRPMData(rpmCategories['ai-tools']);
    setOptimalAdCount(3);
  }, [location.pathname]);

  // Apply RPM optimizations
  const applyOptimizations = useCallback(() => {
    if (!rpmData) return;

    // Update meta tags for better targeting
    const updateMetaTags = () => {
      // Remove existing
      const existing = document.querySelectorAll('meta[name^="adsense-"]');
      existing.forEach(el => el.remove());
      
      // Add optimized tags
      const meta = document.createElement('meta');
      meta.name = 'adsense-rpm-category';
      meta.content = rpmData.category;
      document.head.appendChild(meta);
      
      const keywords = document.createElement('meta');
      keywords.name = 'adsense-target-keywords';
      keywords.content = rpmData.keywords.join(',');
      document.head.appendChild(keywords);
    };

    // Update page title for better CTR
    const updateTitle = () => {
      const title = document.title;
      if (!title.includes('2025') && !title.includes('2026')) {
        document.title = `${title} | 2025 AI Tools`;
      }
    };

    updateMetaTags();
    updateTitle();
  }, [rpmData]);

  useEffect(() => {
    analyzePage();
  }, [analyzePage]);

  useEffect(() => {
    if (rpmData) {
      applyOptimizations();
    }
  }, [rpmData, applyOptimizations]);

  return {
    rpmData,
    optimalAdCount,
    suggestedPlacements: rpmData?.suggestedPlacements || [],
    estimatedRPM: rpmData?.avgRPM || 5.5,
    isHighValuePage: (rpmData?.avgRPM || 0) > 7
  };
};

export default useRPMOptimization;